import { getRuntimeConfig } from './runtimeConfig';

export const ALLOWED_CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

const SENSITIVE_PROPERTY_NAMES = new Set([
  'name',
  'email',
  'phone',
  'company',
  'from',
  'answers',
  'error',
  'errors',
  'message',
  'stack',
  'exception',
]);

const EVENT_PROPERTY_ALLOWLIST = {
  $pageview: new Set(['$current_url', '$referrer', '$pathname']),
  'pricing:billing_period_change': new Set(['billing_period']),
  'pricing:plan_cta_click': new Set(['plan_key', 'billing_period']),
  'lead:form_open': new Set(['plan_key', 'billing_period']),
  'lead:form_validation_fail': new Set(['invalid_fields']),
  'lead:submission_succeed': new Set(['plan_key', 'billing_period']),
  'lead:submission_fail': new Set(['plan_key', 'billing_period']),
  'contact:whatsapp_cta_click': new Set(['placement']),
};

const PLAN_KEYS = new Set(['basic', 'start', 'lite', 'premium_plus']);
const BILLING_PERIODS = new Set([
  'trimestral',
  'semestral',
  'anual',
  'projeto',
]);
const INVALID_FIELDS = new Set(['company', 'name', 'email', 'phone', 'from']);
const PLACEMENTS = new Set(['global']);

const getUrlBase = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'http://localhost';
};

const isAbsoluteUrl = (value) => /^[a-z][a-z\d+.-]*:/i.test(value);

const looksSensitive = (value) => {
  if (typeof value !== 'string') {
    return true;
  }

  return /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value) || /\d{10,}/.test(value);
};

const sanitizePathname = (pathname) => {
  return pathname
    .split('/')
    .map((segment) => {
      try {
        return looksSensitive(decodeURIComponent(segment))
          ? '[redacted]'
          : segment;
      } catch (error) {
        return segment;
      }
    })
    .join('/');
};

export const sanitizeUrl = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return '';
  }

  const wasAbsolute = isAbsoluteUrl(value);
  let parsed;

  try {
    parsed = new URL(value, getUrlBase());
  } catch (error) {
    return '';
  }

  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return '';
  }

  const pathname = sanitizePathname(parsed.pathname || '/');
  const params = new URLSearchParams();

  ALLOWED_CAMPAIGN_PARAMS.forEach((key) => {
    const valueForKey = parsed.searchParams.get(key);

    if (valueForKey && !looksSensitive(valueForKey)) {
      params.set(key, valueForKey);
    }
  });

  const query = params.toString();
  const result = `${pathname}${query ? `?${query}` : ''}`;

  if (!wasAbsolute) {
    return result;
  }

  return `${parsed.origin}${result}`;
};

export const sanitizeCurrentUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return sanitizeUrl(window.location.href);
};

export const sanitizeReferrer = () => {
  if (typeof document === 'undefined') {
    return '';
  }

  return sanitizeUrl(document.referrer);
};

export const planKeyFromTranslationKey = (translationKey) => {
  if (typeof translationKey !== 'string') {
    return undefined;
  }

  const value = translationKey
    .split('.')
    .pop()
    .replace('+', '_plus');

  return PLAN_KEYS.has(value) ? value : undefined;
};

const isSensitivePropertyName = (name) => {
  const normalized = name.toLowerCase().replace(/^\$/, '');

  return (
    SENSITIVE_PROPERTY_NAMES.has(normalized) ||
    normalized.includes('error') ||
    normalized.includes('stack') ||
    normalized.includes('exception')
  );
};

const sanitizeEventProperties = (
  eventName,
  properties = {},
) => {
  const allowedProperties = EVENT_PROPERTY_ALLOWLIST[eventName] || new Set();
  const safeProperties = {};

  Object.entries(properties || {}).forEach(([name, value]) => {
    if (isSensitivePropertyName(name)) {
      return;
    }

    if (name === '$current_url' || name === '$referrer') {
      const sanitized = sanitizeUrl(value);
      if (sanitized) {
        safeProperties[name] = sanitized;
      }
      return;
    }

    if (name === '$pathname') {
      const sanitized = sanitizeUrl(value);
      if (sanitized) {
        safeProperties[name] = new URL(sanitized, getUrlBase()).pathname;
      }
      return;
    }

    if (!allowedProperties.has(name)) {
      return;
    }

    if (name === 'plan_key' && PLAN_KEYS.has(value)) {
      safeProperties[name] = value;
    }

    if (name === 'billing_period' && BILLING_PERIODS.has(value)) {
      safeProperties[name] = value;
    }

    if (name === 'placement' && PLACEMENTS.has(value)) {
      safeProperties[name] = value;
    }

    if (name === 'invalid_fields' && Array.isArray(value)) {
      safeProperties[name] = value.filter((field) => INVALID_FIELDS.has(field));
    }
  });

  const runtimeConfig = getRuntimeConfig();
  safeProperties.deployment_environment = runtimeConfig.deploymentEnvironment;

  if (eventName === '$pageview') {
    safeProperties.$current_url =
      safeProperties.$current_url || sanitizeCurrentUrl();
    safeProperties.$pathname =
      safeProperties.$pathname ||
      new URL(safeProperties.$current_url || '/', getUrlBase()).pathname;
  }

  return safeProperties;
};

const sanitizePostHogEventProperties = (eventName, properties = {}) => {
  const safeProperties = { ...properties };

  Object.keys(safeProperties).forEach((name) => {
    if (isSensitivePropertyName(name)) {
      delete safeProperties[name];
    }
  });

  ['$current_url', '$referrer'].forEach((name) => {
    if (name in safeProperties) {
      const sanitized = sanitizeUrl(safeProperties[name]);
      if (sanitized) {
        safeProperties[name] = sanitized;
      } else {
        delete safeProperties[name];
      }
    }
  });

  if ('$pathname' in safeProperties) {
    const sanitized = sanitizeUrl(safeProperties.$pathname);
    if (sanitized) {
      safeProperties.$pathname = new URL(sanitized, getUrlBase()).pathname;
    } else {
      delete safeProperties.$pathname;
    }
  }

  if (eventName === '$pageview') {
    safeProperties.$current_url =
      safeProperties.$current_url || sanitizeCurrentUrl();
    safeProperties.$pathname =
      safeProperties.$pathname ||
      new URL(safeProperties.$current_url || '/', getUrlBase()).pathname;
  }

  safeProperties.deployment_environment = getRuntimeConfig().deploymentEnvironment;
  return safeProperties;
};

export const sanitizeEvent = (event) => {
  if (!event || typeof event !== 'object') {
    return null;
  }

  return {
    ...event,
    properties: sanitizePostHogEventProperties(event.event, event.properties),
  };
};

export const sanitizeCaptureProperties = (eventName, properties) =>
  sanitizeEventProperties(eventName, properties);
